import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@cp/src/store';
import { Typography } from '@navi/web-ui/lib/primitives';
import NaviTable from '@cp/src/components/NaviTable/NaviTable';
import { PaginationState } from '@tanstack/react-table';
import { getQcAgentAudits, getQcAgentFilters, getQcAuditDetails } from '../action';
import { setSelectedAuditAssignmentReferenceId } from '../callAuditSlice';
import { QcAgentAudit } from '../interface';
import FilterDrawer from '@cp/src/components/FilterDrawer';
import { navigateToCaseDetails } from '@cp/src/utils/commonUtils';
import APP_ROUTES from '@cp/src/layout/Routes';
import { getQcAgentColumns } from './columns';
import { DEFAULT_PAGINATION_STATE } from '../../ExternalDashboardSensei/constants';

const QcAgentView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loading = useSelector((state: RootState) => state.callAudit?.isTableLoading);

  const pageSize = useSelector((state: RootState) => state.callAudit?.pageSize);

  const qcAgentAudits = useSelector((state: RootState) => state.callAudit?.qcAgentAudits);
  const displayTotalElements = useSelector((state: RootState) => state.callAudit?.totalElements);
  const qcAgentFilters = useSelector((state: RootState) => state.callAudit?.qcAgentFilters);

  useEffect(() => {
    dispatch(getQcAgentAudits());
    dispatch(getQcAgentFilters());
  }, []);

  const columns = useMemo(() => getQcAgentColumns(), []);

  const handlePaginationChange = (paginationState: PaginationState) => {
    const newPage = paginationState.pageIndex;
    const newSize = paginationState.pageSize;
    dispatch(getQcAgentAudits({ page: newPage, size: newSize }));
  };

  const handleCaseClick = (data: QcAgentAudit) => {
    dispatch(setSelectedAuditAssignmentReferenceId(data.auditAssignmentReferenceId));
    dispatch(getQcAuditDetails(data.auditAssignmentReferenceId));

    navigateToCaseDetails(
      data?.customerReferenceId,
      data?.caseEntityId,
      data?.caseReferenceId,
      data?.businessVertical,
      navigate,
      APP_ROUTES.CASE_DETAIL.path,
      `mode*callAudit=true&auditId*callAudit=${data.auditAssignmentReferenceId}`
    );
  };

  const filterSchema = useMemo(
    () => ({
      auditStatus: {
        label: 'Audit Status',
        options:
          qcAgentFilters?.auditStatuses?.map(status => ({
            label: status.displayName,
            value: status.status
          })) || [],
        key: 'auditStatus',
        multiSelect: true,
        isSearchEnabled: false
      }
    }),
    [qcAgentFilters]
  );

  const handleApplyFilters = (
    selectedFilters: Record<string, string[] | Record<string, string[]>>
  ) => {
    const auditStatusFilter = selectedFilters?.auditStatus as string[] | undefined;
    dispatch(
      getQcAgentAudits({
        page: 0,
        size: pageSize,
        auditStatusFilter
      })
    );
  };

  return (
    <div className="flex flex-col justify-center w-full">
      <div className="flex flex-row justify-between mb-4 items-center">
        <Typography variant="h2" className="mb-5" color="var(--navi-color-gray-c1)">
          Calls
        </Typography>

        <FilterDrawer
          filterButtonTitle="Filter"
          filterSchema={filterSchema}
          onApply={handleApplyFilters}
          tableName="CALL_AUDIT"
        />
      </div>

      <NaviTable
        data={qcAgentAudits}
        columns={columns}
        totalElements={displayTotalElements}
        showPaginationComponent={true}
        defaultPaginationState={DEFAULT_PAGINATION_STATE}
        onPaginationChange={handlePaginationChange}
        loading={loading}
        enableRowClick={() => true}
        onRowClick={row => handleCaseClick(row as QcAgentAudit)}
        alternateRowColor="var(--greyscale-content-4)"
      />
    </div>
  );
};

export default QcAgentView;
